import NFTDisplayer from '@components/NftDisplayer'
import useBalances from '@plug-core/hooks/useBalances'
import { BTCNFT, ICNFT } from '@plug-core/interfaces/common'
import { Avatar, Button, Divider, Heading, ListGroup, ListRow, Text } from '@ui'
import { ROUTES } from '@utils/routes'
import React, { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { HiArrowTopRightOnSquare } from 'react-icons/hi2'
import { IoIosSend } from 'react-icons/io'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

/** Label on the left, value on the right; long values wrap instead of pushing the label out. */
const KeyValueRow = ({
  label,
  value,
  leading,
}: {
  label: ReactNode
  value?: ReactNode
  leading?: ReactNode
}) => (
  <ListRow
    dense
    leading={leading}
    primary={
      <span className={value !== undefined ? 'font-medium text-fg-2' : undefined}>{label}</span>
    }
  >
    {value !== undefined && (
      <span className="min-w-0 flex-[2] text-right text-[15px] font-medium break-all text-fg">
        {value}
      </span>
    )}
  </ListRow>
)

export const NftDetails = () => {
  const { nftCanisterId } = useParams<{ nftCanisterId: string }>()

  const { t } = useTranslation()

  const {
    state: { item },
  } = useLocation() as { state: { item: ICNFT | BTCNFT } }

  const navigate = useNavigate()
  const { walletNfts } = useBalances()

  const collection = useMemo(() => {
    return walletNfts?.find((collection) => collection.canisterId === nftCanisterId)
  }, [nftCanisterId])

  const nftProperties = useMemo(() => {
    let properties: { label: string; value?: string; img?: string }[] = []

    if (collection) {
      properties.push({ label: collection.name, img: collection.icon })
    }

    if (item.standard === 'ordinal') {
      properties.push({ label: 'ID', value: item.number.toString() })
      properties.push({ label: t('nft.standard'), value: item.standard })
      properties.push({
        label: t('nft.inscribed'),
        value: item.genesisTimestamp ? new Date(item.genesisTimestamp).toLocaleString() : 'N/A',
      })
      properties.push({ label: t('nft.rarity'), value: item.satRarity })
    } else {
      properties.push({ label: 'ID', value: item.index.toString() })
      properties.push({ label: t('nft.standard'), value: item.standard })
    }

    return properties
  }, [collection, item])

  const nftMetadata = useMemo(() => {
    if (item.standard !== 'ordinal') return []

    return Object.entries(item.metadata || {})
  }, [item])

  return (
    <div className="mb-3 flex flex-col gap-4">
      {item && <NFTDisplayer nft={item} />}

      <div className="flex gap-2">
        <Button
          variant="secondary"
          startIcon={<HiArrowTopRightOnSquare size={20} />}
          onClick={() => window.open(item?.url, '_blank')}
          fullWidth
          className="flex-1"
        >
          {t('nft.openAsset')}
        </Button>
        <Button
          variant="primary"
          startIcon={<IoIosSend size={20} />}
          onClick={() => navigate(ROUTES.SEND, { state: { selectedNft: item } })}
          fullWidth
          className="flex-1"
        >
          {t('nft.sendNft')}
        </Button>
      </div>

      <div className="flex flex-col gap-2">
        <Heading level={4} className="px-1">
          {collection ? t('nft.collection') : t('nft.info')}
        </Heading>

        <ListGroup>
          {nftProperties.map(({ label, value, img }) => (
            <KeyValueRow
              key={label}
              label={label}
              value={value}
              leading={img ? <Avatar src={img} size={24} fallback={label} /> : undefined}
            />
          ))}
        </ListGroup>
      </div>

      <Divider className="my-2" />

      <div className="flex flex-col gap-2">
        {collection?.description && (
          <>
            <Heading level={4} className="px-1">
              {t('nft.about')}
            </Heading>
            <Text variant="body-s" tone="fg-2" className="px-1">
              {collection.description}
            </Text>
          </>
        )}
        {nftMetadata.length > 0 && (
          <ListGroup>
            {nftMetadata.map(([key, value]) => (
              <KeyValueRow key={key} label={key} value={value} />
            ))}
          </ListGroup>
        )}
      </div>

      <div className="h-6" />
    </div>
  )
}
